<template>
    <div
        :style="{
            'font-family': pricing.fontFamily,
            'background-color': pricing.transparentBackground
                ? 'transparent'
                : renderPricing.pricingColor,
        }"
    >
        <template v-if="pricing.headerLayout === PRICING_HEADER_LAYOUTS.background">
            <img v-if="pricing.backImg" :src="pricing.backImg" class="h-60 w-full object-cover" />
            <div
                class="h-48 sm:h-60 w-full"
                v-else
                :style="{ background: 'linear-gradient(180deg, #A5C0EE 0%, #FBC5EC 100%)' }"
            ></div>
        </template>
        <div
            class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            :class="[
                pricing.headerLayout === PRICING_HEADER_LAYOUTS.background
                    ? 'pb-12 sm:pb-24'
                    : 'py-12 sm:py-24',
            ]"
        >
            <PricingHeader
                :pricing="pricing"
                :renderPricing="renderPricing"
                :finalLayout="finalLayout"
                :modelValue="currentCategoryIndex"
                @update:modelValue="changeCategoryIndex($event)"
                @changeLayout="$emit('changeLayout', $event)"
            />

            <div
                v-for="(category, index) in categoriesList"
                :key="'category' + category.id"
                :style="{ display: currentCategoryIndex === index ? 'block' : 'none' }"
                class="pt-12 sm:pt-24"
            >
                <BasicLayout
                    v-if="finalLayout === PRICING_LAYOUTS.basic"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <GridLayout
                    v-if="finalLayout === PRICING_LAYOUTS.grid"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <LoopLayout
                    v-if="finalLayout === PRICING_LAYOUTS.loop"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <ListLayout
                    v-if="finalLayout === PRICING_LAYOUTS.list"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <LinksLayout
                    v-if="finalLayout === PRICING_LAYOUTS.links"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <SharedLayout
                    v-if="finalLayout === PRICING_LAYOUTS.shared"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <BasicBottomLayout
                    v-if="finalLayout === PRICING_LAYOUTS.basicBottom"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
                <GridBottomLayout
                    v-if="finalLayout === PRICING_LAYOUTS.gridBottom"
                    :pricing="pricing"
                    :variants="renderVariants(index)"
                />
            </div>

            <div class="mt-6 sm:mt-12 text-center" v-if="renderPricing.linkName">
                <a
                    :href="renderPricing.linkHref"
                    @click.prevent="goToLink(renderPricing.linkHref)"
                    type="button"
                    :style="{
                        color: renderPricing.headingsColor,
                        'box-shadow': `0 0 0 0px #fff, 0 0 0 1px ${renderPricing.borderColor}, 0 0 #0000`,
                    }"
                    class="inline-block pricing-btn px-4 py-2 text-center rounded-md text-base font-medium focus:z-10 focus:outline-none opacity-90 hover:opacity-100"
                >
                    <cUtilsEdit @edit="goToLayoutField('pricingLinkName')">
                        <span>{{ renderPricing.linkName }}</span>
                    </cUtilsEdit>
                </a>
            </div>
            <div
                v-if="!hideLogo && pricing.showFooter"
                class="w-full mt-6 sm:mt-12 space-x-3 flex items-center justify-center"
            >
                <EasyLogoIcon class="flex-shrink-0 text-pricing-500 h-8 w-8" />
                <a
                    target="_blank"
                    :style="{ color: pricing.titleColor }"
                    href="https://easy.tools/offer"
                    @click.prevent="goToLink('https://easy.tools/offer')"
                    class="text-sm"
                >
                    {{ $t("r_shared.made_with") }}
                    <span class="underline">Easyoffer</span>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import ListLayout from "./ListLayout.vue";
import BasicLayout from "./BasicLayout.vue";
import GridLayout from "./GridLayout.vue";
import LoopLayout from "./LoopLayout.vue";
import LinksLayout from "./LinksLayout.vue";
import SharedLayout from "./SharedLayout.vue";
import BasicBottomLayout from "./BasicBottomLayout.vue";
import GridBottomLayout from "./GridBottomLayout.vue";
import PricingHeader from "./PricingHeader.vue";
import { cloneDeep } from "@/shared/utils/helpers.js";
import { EasyLogoIcon } from "@easy/ui/icons";
import { PRICING_LAYOUTS, PRICING_HEADER_LAYOUTS } from "@/apps/pricing/services/pricing.service";
import { usePricingGoTo } from "@/views/tools/pricing/utils/usePricingGoTo";
import { useGoToLink } from "@/shared/utils/router/useGoToLink";
import cUtilsEdit from "@/views/shared/cUtilsEdit.vue";

export default {
    name: "PricingBody",
    components: {
        BasicLayout,
        GridLayout,
        LoopLayout,
        ListLayout,
        LinksLayout,
        SharedLayout,
        BasicBottomLayout,
        GridBottomLayout,
        PricingHeader,
        cUtilsEdit,
        EasyLogoIcon,
    },
    props: {
        pricing: Object,
        hideLogo: Boolean,
    },
    setup() {
        const { goToLink } = useGoToLink();
        const { goToLayoutField } = usePricingGoTo();
        return {
            goToLink,
            goToLayoutField,
        };
    },
    emits: ["updateIframeHeight"],
    data() {
        return {
            currentBackColor: "",
            currentCategoryIndex: 0,
            currentView: this.$route.query.layout,
            PRICING_LAYOUTS,
            PRICING_HEADER_LAYOUTS,
        };
    },
    computed: {
        renderPricing() {
            const renderPricing = cloneDeep(this.pricing || {});
            if (this.pricing?.ecPricing) {
                for (const key in this.pricing?.ecPricing || {}) {
                    if (this.pricing.ecPricing[key]) {
                        renderPricing[key] = renderPricing[key]
                            ? renderPricing[key]
                            : this.pricing.ecPricing[key];
                    }
                }
            }
            return renderPricing;
        },
        pricingHeadingsColor() {
            return this.pricing.headingsColor;
        },
        pricingTitleColor() {
            return this.pricing.titleColor;
        },
        pricingBackColor() {
            return this.pricing.backColor;
        },
        pricingPricingColor() {
            return this.pricing.pricingColor;
        },
        finalLayout() {
            return this.currentView || this.renderPricing.layout;
        },
        categoriesList() {
            return this.pricing.categories?.filter((category) => !category._deleted) || [];
        },
    },
    watch: {
        pricingBackColor: {
            handler() {
                this.setColors();
            },
            immediate: true,
        },
        pricingPricingColor: {
            handler() {
                this.setColors();
            },
            immediate: true,
        },
        pricingHeadingsColor: {
            handler() {
                this.setColors();
            },
            immediate: true,
        },
        pricingTitleColor: {
            handler() {
                this.setColors();
            },
            immediate: true,
        },
    },
    methods: {
        setColors() {
            const newStyle = document.createElement("style");
            newStyle.innerHTML = `
            .pricing-btn {
                background-color: ${this.pricing.backColor} !important;
                color: ${this.pricing.headingsColor} !important;
            }
            .pricing-btn:hover {
                background-color: ${this.pricing.pricingColor} !important;
                color: ${this.pricing.titleColor} !important;
              }
            `;

            const existingStyles = document.head.querySelectorAll("style");

            if (existingStyles.length) existingStyles.forEach((style) => style.remove());

            setTimeout(() => {
                document.head.appendChild(newStyle);
            }, 100);
        },
        categoryVariantsClone(index) {
            const currentCategory = this.pricing.categories[index] || {};
            return cloneDeep(currentCategory.variants || [])
                .filter((variant) => !variant.hide && !variant._deleted)
                .sort((a, b) => {
                    return a.order - b.order;
                });
        },
        updateVariantWithEcData(variant) {
            const ecVariant = this.pricing.ecVariants.find((x) => x.ecId === variant.ecId);

            if (ecVariant) {
                for (const key in ecVariant) {
                    if (key in variant) {
                        variant[key] = variant[key] ? variant[key] : ecVariant[key];
                    }
                }
            }

            return variant;
        },
        formattedPoints(points) {
            return points
                ? points.split("\n").map((point) => {
                      const regex1 = /^\+/gm;
                      const regex2 = /^x/gm;
                      return {
                          name: point.replace(regex1, "").replace(regex2, "").trim(),
                          positive: point.match(regex1) !== null,
                          negative: point.match(regex2) !== null,
                      };
                  })
                : [];
        },
        renderVariants(index) {
            const renderVariants = this.categoryVariantsClone(index);

            renderVariants.forEach((variant) => {
                if (this.pricing.ecId) {
                    variant = this.updateVariantWithEcData(variant);
                }
                variant.features = this.formattedPoints(variant.points);
            });
            return renderVariants;
        },
        async changeCategoryIndex(index) {
            this.currentCategoryIndex = index;
            await this.$nextTick();
            this.$emit("updateIframeHeight");
        },
    },
};
</script>
